import { readDetails } from "./companyDetails";
import { isPermissionGranted } from "./navPermissions";
import { PagesPermissions } from "../types/Authorization";
import type { AttachAction } from "./askAttachmentPlan";
import { currencyOf, linesFromText } from "./priceList";
import { hasEmailHeader, messageSenders, stripMessage, withoutChatStamps } from "./stripMessage";
import { RecordKind, SimilarRecord, findSimilarRecord } from "./similarRecord";
import type { AfterCreateStep } from "./afterCreate";

/** A record page the pasted details could update. */
export type UpdatableKind = RecordKind;

/** What pasted text is, read by code on this computer. */
export type PastedKind = "price_list" | "fleet_list" | "business" | "message";

const RECORD_PAGES: Array<[RegExp, UpdatableKind]> = [
    [/^\/provider\/(\d+)(?:\/|$)/, "provider"],
    [/^\/client\/(\d+)(?:\/|$)/, "client"],
    [/^\/company\/(\d+)(?:\/|$)/, "company"],
    [/^\/lead\/(\d+)(?:\/|$)/, "lead"],
];

const EDIT_RIGHT: Record<UpdatableKind, string> = {
    provider: PagesPermissions.PROVIDERS_EDIT,
    client: PagesPermissions.CLIENTS_EDIT,
    company: PagesPermissions.COMPANIES_EDIT,
    lead: PagesPermissions.LEADS_EDIT,
    contact: PagesPermissions.CONTACTS_EDIT,
};

/** The company, client, provider or lead record open at this address, if any. */
export const recordFromPath = (pathname: string): { kind: UpdatableKind; id: number } | null => {
    for (const [pattern, kind] of RECORD_PAGES) {
        const match = pattern.exec(pathname);
        if (match) return { kind, id: Number(match[1]) };
    }
    return null;
};

/**
 * Whether typed or pasted text carries someone's company or contact details —
 * a signature, a business card, an introduction email: a company or a name,
 * and at least two ways to reach them (email, phone, website). Read by code,
 * on this computer.
 * @param text - What the person typed or pasted.
 */
export const looksLikeBusinessText = (text: string): boolean => {
    const lines = String(text ?? "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length < 3) return false;
    const details = readDetails(text);
    const reach = details.emails.length + details.phones.length + (details.website ? 1 : 0);
    return reach >= 2 && !!(details.company || details.person);
};

/**
 * Whether pasted text is a price list: three or more lines that each carry a
 * price with its currency or unit (read by code, on this computer — the same
 * reader as the price sheets), so times and counts in a message don't count.
 * @param text - What the person typed or pasted.
 */
export const looksLikePriceList = (text: string): boolean => {
    const lines = linesFromText(String(text ?? ""), { services: [], places: [], resources: [] }).filter((line) => line.amount !== null);
    // a report or brief that quotes a few amounts isn't a price list: priced lines must be most of the text, and it isn't prose
    // WhatsApp stamps and sender names off first: they made short rate lines count as prose
    const written = withoutChatStamps(String(text ?? "")).split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const prose = written.filter((line) => line.split(/\s+/).length >= 15).length;
    if (prose >= 3 || lines.length < written.length * 0.4) return false;
    if (lines.filter((line) => line.currency).length >= 3) return true;
    // "Rates (AED)": a currency named once, then priced lines with units
    return !!currencyOf(String(text ?? "")) && lines.filter((line) => line.unit).length >= 3;
};

const VEHICLE_WORDS = /\b(?:toyota|land\s?cruiser|lexus|nissan|patrol|chevrolet|suburban|tahoe|gmc|yukon|ford|mercedes|benz|bmw|audi|hiace|hilux|prado|range rover|land rover|cadillac|escalade|suv|sedan|van|armou?red|b[4-7]|soft\s?skin)\b|تويوتا|مرسيدس|مصفح/i;
const PLATE_WORD = /\b(?:plate|reg(?:istration)?\.?\s*(?:no|number))\b|لوحة/i;
const PLATE_TOKEN = /\b[A-Z]{1,2}\s?\d{4,6}\b/;

/**
 * Whether pasted text is a fleet list: two or more lines that each name a
 * vehicle and its plate. A client asking for "2 armoured SUVs" has no plates.
 * @param text - What the person typed or pasted.
 */
export const looksLikeFleetList = (text: string): boolean =>
    String(text ?? "").split(/\r?\n/).filter((line) => VEHICLE_WORDS.test(line) && (PLATE_WORD.test(line) || PLATE_TOKEN.test(line))).length >= 2;

/**
 * What pasted text is: a price list, a fleet list, someone's company or
 * contact details, a chat or email from someone — or an ordinary question.
 * @param text - What the person typed or pasted.
 */
export const pastedKind = (text: string): PastedKind | null => {
    if (looksLikePriceList(text)) return "price_list";
    if (looksLikeFleetList(text)) return "fleet_list";
    // a chat or an email (its header pasted too) is a message, even with a signature full of details
    if (messageSenders(text).length || hasEmailHeader(text)) return "message";
    if (looksLikeBusinessText(text)) return "business";
    return hasSignOff(text) ? "message" : null;
};

/** Pasted text that ends in a sign-off or carries a quoted reply (something comes off, something stays). */
const hasSignOff = (text: string): boolean => {
    if (String(text ?? "").split(/\r?\n/).filter((line) => line.trim()).length < 3) return false;
    const flat = (value: string) => value.replace(/\s+/g, " ").trim();
    const kept = flat(stripMessage(text, 100000));
    return !!kept && kept !== flat(text);
};

/**
 * What goes to the AI for pasted text (D18): a chat or an email loses its
 * time stamps, sender names, header, quoted replies and signature. Other
 * kinds go as pasted; the names read by code go beside them to be masked.
 * @param text - What was pasted.
 * @param kind - What it is.
 */
export const textForAi = (text: string, kind: PastedKind | null): string =>
    kind === "message" ? stripMessage(text, 1500) || text : text;

/**
 * The people's and companies' names read by code in pasted text — sent beside
 * the question so the server turns them into codes even when they aren't in
 * CRX yet (D18: new names never reach the AI); and who it's from.
 * @param text - What was pasted.
 */
export const pastedNames = (text: string): { people: string[]; companies: string[]; sender: { person: string | null; company: string | null } } => {
    const details = readDetails(text);
    const unique = (values: Array<string | null | undefined>) => Array.from(new Set(values.map((value) => String(value ?? "").trim()).filter((value) => value.length >= 3))).slice(0, 6);
    return {
        // the English spelling CRX fills in and the Arabic as written: the pasted text may carry either
        people: unique([...messageSenders(text).filter((sender) => !/^\+?[\d\s().-]{8,}$/.test(sender)), details.person, details.personArabic]),
        companies: unique([details.company, details.companyArabic, details.legalName, details.tradeName]),
        sender: { person: details.person, company: details.company },
    };
};

const label = (record: SimilarRecord) => `${record.name}${record.exact ? "" : " (predicted)"}`;
const checkIt = (record: SimilarRecord) => (record.exact ? "" : " It's the closest match to the name read — check it's the right one.");

/** A record the person has open, as { kind, id }. */
type OpenRecord = { kind: UpdatableKind; id: number };

/**
 * The record on screen, only when the pasted text is about it: the text names
 * no company (a phone, an email, a note), or the company it names IS that
 * record. Another company's details never update the record you happen to be on.
 * @param pathname - Where the person is.
 * @param company - The company read from the text, if any.
 * @param found - The record that company points to in CRX, if any.
 */
export const openIfAbout = (pathname: string, company: string | null, found: SimilarRecord | null): OpenRecord | null => {
    const open = recordFromPath(pathname);
    if (!open) return null;
    if (!company) return open;
    return found && found.kind === open.kind && found.id === open.id ? open : null;
};

/**
 * What the person can do with a pasted price list: put it on the provider's
 * rate sheet (the one on screen when the list is its, else the one its
 * signature names), start a new provider when CRX has none by that name, or
 * pick the sheet. Read on this computer with the real amounts.
 * @param text - The pasted text.
 * @param onScreen - The provider on screen, only when the list is about it.
 * @param pages - Their page permissions.
 * @param named - Another provider the text names, if found.
 */
export const priceListActions = (text: string, onScreen: number | null, pages: any[], named: SimilarRecord | null = null): AttachAction[] => {
    const can = (right: string) => isPermissionGranted(pages, right);
    const ratesRight = can(PagesPermissions.PROVIDERS_EDIT) && can(PagesPermissions.PROVIDERS_FINANCE);
    if (onScreen && ratesRight) {
        return [{
            label: "Fill with AI: this provider's rates", icon: "stars",
            title: "Open this provider's rate sheet with these lines read on your computer — you tick each line; nothing is saved until you save the sheet.",
            go: { type: "price_list_text", text, sheet: "provider", recordId: onScreen },
        }];
    }
    const actions: AttachAction[] = [];
    if (named && ratesRight) {
        actions.push({
            label: `Fill with AI: ${label(named)}'s rates`, icon: "stars",
            title: `Open ${named.name}'s rate sheet with these lines read on your computer — you tick each line; nothing is saved until you save the sheet.${checkIt(named)}`,
            go: { type: "price_list_text", text, sheet: "provider", recordId: named.id },
        });
    } else if (!named && readDetails(text).company && can(PagesPermissions.PROVIDERS_CREATE)) {
        actions.push(newProvider(text, { type: "price_list", text }, "its rate sheet then opens with these lines"));
    }
    if (can(PagesPermissions.PROVIDERS_EDIT) || can(PagesPermissions.MISSION_REQUESTS_EDIT) || can(PagesPermissions.MISSIONS_EDIT)) {
        actions.push({
            label: "Fill with AI: price sheet", icon: "stars",
            title: "Pick the provider, request or mission sheet; the lines open there, read on your computer, to tick and correct. Nothing is saved until you save the sheet.",
            go: { type: "price_list_text", text },
        });
    }
    return actions;
};

/** A new provider from the text's company details; once it's saved, what comes next (its rates, its fleet) opens. */
const newProvider = (text: string, then: AfterCreateStep, next: string): AttachAction => ({
    label: `Fill with AI: new provider + ${then.type === "fleet_list" ? "fleet" : "rates"}`, icon: "stars",
    title: `No provider by that name in CRX: open a new provider with Fill with AI reading the company details on your computer — you tick what goes in and save; ${next}.`,
    go: { type: "record_new", kind: "provider", text, then: [then] },
});

/**
 * What the person can do with a pasted fleet list: put it in the provider's
 * register (the one on screen when the list is its, else the one it names),
 * or start that provider.
 * @param text - The pasted text.
 * @param onScreen - The provider on screen, only when the list is about it.
 * @param pages - Their page permissions.
 * @param named - Another provider the text names, if found.
 */
export const fleetListActions = (text: string, onScreen: number | null, pages: any[], named: SimilarRecord | null = null): AttachAction[] => {
    const can = (right: string) => isPermissionGranted(pages, right);
    const target = onScreen ? { id: onScreen, name: "this provider", record: null } : named ? { id: named.id, name: label(named), record: named } : null;
    if (target && can(PagesPermissions.PROVIDERS_EDIT)) {
        return [{
            label: `Fill with AI: ${target.name}'s fleet`, icon: "stars",
            title: `Open the provider's assets with this list ready to read — you tick each vehicle and item; nothing is saved until you save.${target.record ? checkIt(target.record) : ""}`,
            go: { type: "fleet_text", providerId: target.id, text },
        }];
    }
    return !target && readDetails(text).company && can(PagesPermissions.PROVIDERS_CREATE) ? [newProvider(text, { type: "fleet_list", text }, "its assets then open with this list")] : [];
};

/**
 * What the person can do with pasted company or contact details, or a chat or
 * email from someone, each read on this computer (D18 — new names never reach
 * the AI): update the record it's about (the one on screen when it's about
 * it, else the one it names), start a lead, start a contact. Only what their
 * rights allow.
 * @param text - The pasted text.
 * @param onScreen - The record on screen, only when the text is about it.
 * @param pages - Their page permissions.
 * @param named - Another record the text names, if found.
 */
export const businessActions = (text: string, onScreen: OpenRecord | null, pages: any[], named: SimilarRecord | null = null): AttachAction[] => {
    const can = (right: string) => isPermissionGranted(pages, right);
    const actions: AttachAction[] = [];
    if (onScreen && can(EDIT_RIGHT[onScreen.kind])) {
        actions.push({
            label: `Fill with AI: update this ${onScreen.kind}`, icon: "stars",
            title: `Open this ${onScreen.kind} with Fill with AI reading these details on your computer — each change shows as current → new to tick; nothing is saved until you save.`,
            go: { type: "record_update", kind: onScreen.kind, id: onScreen.id, text },
        });
    } else if (named && can(EDIT_RIGHT[named.kind])) {
        actions.push({
            label: `Fill with AI: update ${label(named)}`, icon: "stars",
            title: `Open ${named.name} (${named.kind}) with Fill with AI reading these details on your computer — each new or changed detail shows as current → new to tick; nothing is saved until you save.${checkIt(named)}`,
            go: { type: "record_update", kind: named.kind, id: named.id, text },
        });
    }
    // a company not in CRX yet, with its person: the client or provider and its contact, one after the other
    const details = readDetails(text);
    if (!onScreen && !named && details.company && details.person) {
        const side = details.side;
        if (side !== "provider" && can(PagesPermissions.CLIENTS_CREATE)) {
            actions.push({
                label: "Fill with AI: new client + contact", icon: "stars",
                title: `Open a new client with ${details.company}'s details read on your computer; once you save it, a new contact for ${details.person} opens filled in and linked to it.`,
                go: { type: "record_new", kind: "client", text, then: [{ type: "contact", text }] },
            });
        }
        if (side !== "client" && can(PagesPermissions.PROVIDERS_CREATE)) {
            actions.push({
                label: "Fill with AI: new provider + contact", icon: "stars",
                title: `Open a new provider with ${details.company}'s details read on your computer; once you save it, a new contact for ${details.person} opens filled in and linked to it.`,
                go: { type: "record_new", kind: "provider", text, then: [{ type: "contact", text }] },
            });
        }
    }
    // already in CRX (on screen or found): no new lead for it
    if (!onScreen && !named && can(PagesPermissions.LEADS_CREATE)) {
        actions.push({
            label: "Fill with AI: new lead", icon: "stars",
            title: "Open a new lead with Fill with AI reading the company, person, phones, email, website and what they offer on your computer — you tick what goes in and save.",
            go: { type: "lead", text },
        });
    }
    if (can(PagesPermissions.CONTACTS_CREATE)) {
        actions.push({
            label: "Fill with AI: new contact", icon: "stars",
            title: "Open a new contact with Fill with AI reading the name, title, phones and email on your computer — you tick what goes in and save.",
            go: { type: "contact", text },
        });
    }
    return actions;
};

/**
 * The buttons under Ask's answer for pasted text. The company it names is
 * looked up first (searched as the person — only records they can open); the
 * record on screen is offered only when the text is about it.
 * @param text - What was pasted.
 * @param kind - What it is.
 * @param pathname - Where the person is.
 * @param pages - Their page permissions.
 */
export const pastedActions = async (text: string, kind: PastedKind, pathname: string, pages: any[]): Promise<AttachAction[]> => {
    const details = readDetails(text);
    const company = details.company;
    // a message from no one in particular ("Thanks — Layla"): nothing to file
    if (kind === "message" && !company && !details.person) return [];
    const isList = kind === "price_list" || kind === "fleet_list";
    const found = company ? await findSimilarRecord(company, isList ? ["provider"] : ["provider", "client", "company", "lead"]).catch(() => null) : null;
    // a chat or email names no company: it isn't about the record on screen by itself
    const onScreen = kind === "message" && !company ? null : openIfAbout(pathname, company, found);
    const named = found && !(onScreen && found.kind === onScreen.kind && found.id === onScreen.id) ? found : null;
    if (isList) {
        const provider = onScreen?.kind === "provider" ? onScreen.id : null;
        return kind === "price_list" ? priceListActions(text, provider, pages, named) : fleetListActions(text, provider, pages, named);
    }
    return businessActions(text, onScreen, pages, named);
};

/**
 * The provider the person's next words name, for a pasted price or fleet list
 * Ask couldn't place ("it's App Review Demo Provider's").
 */
export const followUpActions = async (text: string, kind: PastedKind, words: string, pages: any[]): Promise<AttachAction[]> => {
    if (kind !== "price_list" && kind !== "fleet_list") return [];
    const provider = await findSimilarRecord(words, ["provider"]).catch(() => null);
    if (!provider) return [];
    const actions = kind === "price_list" ? priceListActions(text, null, pages, provider) : fleetListActions(text, null, pages, provider);
    return actions.filter((action) => action.go.type !== "record_new" && !(action.go.type === "price_list_text" && !action.go.recordId));
};
